import { useMemo } from 'react';
import { getUrlParam, resolveFixedHeight } from '@utils/functions';
import { UrlParams, Version, T, DefaultStatsConsoleTab } from '@utils/enums';

export interface UrlParamsData {
  // Basic parameters
  eventId: string | null;
  theme: T.LIGHT | T.DARK | null;
  defSCTab: DefaultStatsConsoleTab | null;
  fixtures: boolean | null;
  isMVP: boolean | null;
  fixedHeight: string | null;
  langMonit: boolean | null;
  isDynamicColors: boolean | null;
  version: Version | null;
  
  // Default values
  defaultTheme: T.LIGHT | T.DARK;
  defaultDefSCTab: DefaultStatsConsoleTab;
  defaultFixtures: boolean;
  defaultIsMVP: boolean;
  defaultFixedHeight: string;
  defaultLangMonit: boolean;
  defaultIsDynamicColors: boolean;
  defaultVersion: Version;
  defaultIsIOS: boolean;
  
  // Values with fallback
  themeWithFallback: T.LIGHT | T.DARK;
  defSCTabWithFallback: DefaultStatsConsoleTab;
  fixturesWithFallback: boolean;
  isMVPWithFallback: boolean;
  fixedHeightWithFallback: string;
  langMonitWithFallback: boolean;
  isDynamicColorsWithFallback: boolean;
  versionWithFallback: Version;
  isIOS: boolean;
  
  // Helper functions
  hasEventId: boolean;
  hasTheme: boolean;
  hasDefSCTab: boolean;
  hasFixtures: boolean;
  hasIsMVP: boolean;
  hasFixedHeight: boolean;
  hasLangMonit: boolean;
  hasIsDynamicColors: boolean;
  hasVersion: boolean;
  
  // All parameters as an object
  allParams: Record<string, string | null>;
}

export const useUrlParams = (): UrlParamsData => {
  const urlParams = useMemo(() => {
    // Getting all URL parameters
    const eventId = getUrlParam(UrlParams.EVENT_ID);
    const themeParam = getUrlParam(UrlParams.THEME);
    const defSCTabParam = getUrlParam(UrlParams.DEFAULT_STATS_CONSOLE_TAB);
    const fixtures = getUrlParam(UrlParams.FIXTURES);
    const isMVP = getUrlParam(UrlParams.IS_MVP);
    const fixedHeight = resolveFixedHeight();
    const langMonit = getUrlParam(UrlParams.LANG_MONITORING);
    const isDynamicColors = getUrlParam(UrlParams.DYNAMIC_COLORS);
    const versionParam = getUrlParam(UrlParams.VERSION);
    const iosParam = getUrlParam(UrlParams.IOS);

    // Convert string parameters to enum values
    const theme: T.LIGHT | T.DARK | null = themeParam === T.DARK ? T.DARK : themeParam === T.LIGHT ? T.LIGHT : null;
    const defSCTab = defSCTabParam === DefaultStatsConsoleTab.STATS ? DefaultStatsConsoleTab.STATS :
                    defSCTabParam === DefaultStatsConsoleTab.OVERVIEW ? DefaultStatsConsoleTab.OVERVIEW :
                    defSCTabParam === DefaultStatsConsoleTab.PLAYERS ? DefaultStatsConsoleTab.PLAYERS :
                    defSCTabParam === DefaultStatsConsoleTab.LINEUP ? DefaultStatsConsoleTab.LINEUP :
                    defSCTabParam === DefaultStatsConsoleTab.H2H ? DefaultStatsConsoleTab.H2H : null;
    const version = versionParam === Version.V1 ? Version.V1 :
                   versionParam === Version.V2 ? Version.V2 : null;

    // Default values based on README
    const defaultTheme: T.LIGHT | T.DARK = T.LIGHT;
    const defaultDefSCTab: DefaultStatsConsoleTab = DefaultStatsConsoleTab.STATS;
    const defaultFixtures = false;
    const defaultIsMVP = true;
    const defaultFixedHeight = '341px';
    const defaultLangMonit = false;
    const defaultIsDynamicColors = true;
    const defaultVersion: Version = Version.V2;
    const defaultIsIOS = false;
    // Conversion of string to boolean
    const fixturesBoolean = fixtures ? fixtures === 'true' : null;
    const isMVPBoolean = isMVP ? isMVP === 'true' : null;
    const langMonitBoolean = langMonit ? langMonit === 'true' : null;
    const isDynamicColorsBoolean = isDynamicColors ? isDynamicColors === 'true' : null;
    const isIOSBoolean = iosParam ? iosParam === 'true' : null;
    // Values with fallback
    const themeWithFallback = theme || defaultTheme;
    const defSCTabWithFallback = defSCTab || defaultDefSCTab;
    const fixturesWithFallback = fixturesBoolean ?? defaultFixtures;
    const isMVPWithFallback = isMVPBoolean ?? defaultIsMVP;
    const fixedHeightWithFallback = fixedHeight || defaultFixedHeight;
    const langMonitWithFallback = langMonitBoolean ?? defaultLangMonit;
    const isDynamicColorsWithFallback = isDynamicColorsBoolean ?? defaultIsDynamicColors;
    const versionWithFallback = version || defaultVersion;
    const isIOS = isIOSBoolean ?? defaultIsIOS;
    // Checking if parameters exist
    const hasEventId = eventId !== null;
    const hasTheme = theme !== null;
    const hasDefSCTab = defSCTab !== null;
    const hasFixtures = fixtures !== null;
    const hasIsMVP = isMVP !== null;
    const hasFixedHeight = fixedHeight !== null;
    const hasLangMonit = langMonit !== null;
    const hasIsDynamicColors = isDynamicColors !== null;
    const hasVersion = version !== null;
    const hasIsIOS = iosParam !== null;
    // All parameters as an object
    const allParams: Record<string, string | null> = {
      [UrlParams.EVENT_ID]: eventId,
      [UrlParams.THEME]: theme,
      [UrlParams.DEFAULT_STATS_CONSOLE_TAB]: defSCTab,
      [UrlParams.FIXTURES]: fixtures,
      [UrlParams.IS_MVP]: isMVP,
      [UrlParams.FIXED_HEIGHT]: fixedHeight,
      [UrlParams.LANG_MONITORING]: langMonit,
      [UrlParams.DYNAMIC_COLORS]: isDynamicColors,
      [UrlParams.VERSION]: version,
      [UrlParams.IOS]: iosParam,
    };

    return {
      // Basic parameters
      eventId,
      theme,
      defSCTab,
      fixtures: fixturesBoolean,
      isMVP: isMVPBoolean,
      fixedHeight,
      langMonit: langMonitBoolean,
      isDynamicColors: isDynamicColorsBoolean,
      version,
      // Default values
      defaultTheme,
      defaultDefSCTab,
      defaultFixtures,
      defaultIsMVP,
      defaultFixedHeight,
      defaultLangMonit,
      defaultIsDynamicColors,
      defaultVersion,
      defaultIsIOS,
      // Values with fallback
      themeWithFallback,
      defSCTabWithFallback,
      fixturesWithFallback,
      isMVPWithFallback,
      fixedHeightWithFallback,
      langMonitWithFallback,
      isDynamicColorsWithFallback,
      versionWithFallback,
      isIOS,
      // Helper functions
      hasEventId,
      hasTheme,
      hasDefSCTab,
      hasFixtures,
      hasIsMVP,
      hasFixedHeight,
      hasLangMonit,
      hasIsDynamicColors,
      hasVersion,
      hasIsIOS,
      // All parameters as an object
      allParams,
    };
  }, []);

  return urlParams;
};
